import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { getSocket } from "../socket";

/**
 * TasksFeedPanel (instant-show)
 * Props:
 *  - visibleProjects: [{ _id, name/projectName }]
 *  - refreshSignal: number
 *  - projectIdFilter?: string | null
 *  - assigneeFilter?: string[] | null
 */
export default function TasksFeedPanel({
  visibleProjects = [],
  refreshSignal = 0,
  projectIdFilter = null,
  assigneeFilter = null,
}) {
  const [recentAssigned, setRecentAssigned] = useState([]);
  const [recentCompleted, setRecentCompleted] = useState([]);

  // last good in-memory snapshot (persists across in-flight updates)
  const lastGoodAssigned = useRef([]);
  const lastGoodCompleted = useRef([]);

  // controller to cancel older fetches
  const fetchAbortRef = useRef(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  /* ---------------- helpers ---------------- */
  const normId = (v) =>
    typeof v === "object" && v !== null
      ? v._id || v.id || v.userId || ""
      : v || "";

  const taskAssigneeId = (t) =>
    String(t.assignedToId || normId(t.assignedTo) || t.userId || "");

  const taskProjectId = (t) => String(t.projectId || normId(t.project) || "");

  const passesFilters = (t) => {
    const pid = taskProjectId(t);
    const aid = taskAssigneeId(t);

    if (projectIdFilter && String(pid) !== String(projectIdFilter))
      return false;

    if (
      assigneeFilter &&
      Array.isArray(assigneeFilter) &&
      assigneeFilter.length
    ) {
      if (!assigneeFilter.map(String).includes(String(aid))) return false;
    }
    return true;
  };

  const inVisibleProjects = (pid) =>
    visibleProjects.some((p) => String(p._id) === String(pid));

  const withProjName = (t) => {
    const pid = taskProjectId(t);
    const proj = visibleProjects.find((p) => String(p._id) === pid);
    return {
      ...t,
      projectId: pid,
      projectName:
        t.projectName || proj?.projectName || proj?.name || "Project",
    };
  };

  const sortAssigned = (arr) =>
    [...arr].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );

  const sortCompleted = (arr) =>
    [...arr].sort(
      (a, b) =>
        new Date(b.completedAt || b.updatedAt || b.createdAt || 0) -
        new Date(a.completedAt || a.updatedAt || a.createdAt || 0)
    );

  const dedupeById = (arr) => {
    const seen = new Set();
    const out = [];
    for (const x of arr) {
      const id = String(x._id || "");
      if (!seen.has(id)) {
        seen.add(id);
        out.push(x);
      }
    }
    return out;
  };

  /* ---------------- cache key + readiness ---------------- */
  // stable key — only ids
  const vpKey = useMemo(
    () =>
      visibleProjects
        .map((p) => String(p._id))
        .sort()
        .join(","),
    [visibleProjects]
  );

  // combine into one cache key so each filter state has its own snapshot
  const cacheKey = useMemo(() => {
    const aKey =
      assigneeFilter && assigneeFilter.length
        ? assigneeFilter.map(String).sort().join(",")
        : "all";
    const pKey = projectIdFilter ? String(projectIdFilter) : "all";
    return `tasksFeed:${vpKey}|project=${pKey}|assignee=${aKey}`;
  }, [vpKey, projectIdFilter, assigneeFilter]);

  const ready = useMemo(() => {
    if (!visibleProjects.length) return false;
    if (projectIdFilter) {
      return visibleProjects.some(
        (p) => String(p._id) === String(projectIdFilter)
      );
    }
    return true;
  }, [vpKey, projectIdFilter, visibleProjects]);

  /* ---------------- rehydrate from session cache (instant) ---------------- */
  useEffect(() => {
    // First paint: show cached rows instantly (if any)
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        const a = Array.isArray(parsed.assigned) ? parsed.assigned : [];
        const c = Array.isArray(parsed.completed) ? parsed.completed : [];
        if (a.length || c.length) {
          lastGoodAssigned.current = a;
          lastGoodCompleted.current = c;
          // don't trigger a big re-render storm if we already have similar state
          setRecentAssigned((prev) => (prev.length ? prev : a));
          setRecentCompleted((prev) => (prev.length ? prev : c));
        }
      }
    } catch {
      /* ignore cache errors */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  /* ---------------- fetch (fast + cancelable + no flicker) ---------------- */
  const fetchFeeds = async () => {
    if (!ready) return;

    // cancel any previous run
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    const targets = projectIdFilter
      ? visibleProjects.filter((p) => String(p._id) === String(projectIdFilter))
      : visibleProjects;

    const headersObj = { headers, signal: controller.signal };

    const pendingBucket = [];
    const completedBucket = [];

    // Run all requests in parallel across projects (2 per project)
    const reqs = [];
    for (const p of targets) {
      reqs.push(
        api
          .get(`/tasks/project/${p._id}?status=pending`, headersObj)
          .then((res) => {
            const arr = Array.isArray(res.data) ? res.data : [];
            for (const t of arr) {
              const item = withProjName({ ...t });
              if (passesFilters(item)) pendingBucket.push(item);
            }
          })
          .catch(() => {})
      );
      reqs.push(
        api
          .get(`/tasks/project/${p._id}?status=completed`, headersObj)
          .then((res) => {
            const arr = Array.isArray(res.data) ? res.data : [];
            for (const t of arr) {
              const item = withProjName({ ...t });
              if (passesFilters(item)) completedBucket.push(item);
            }
          })
          .catch(() => {})
      );
    }

    await Promise.allSettled(reqs);
    if (controller.signal.aborted) return;

    const nextAssigned = sortAssigned(dedupeById(pendingBucket)).slice(0, 6);
    const nextCompleted = sortCompleted(dedupeById(completedBucket)).slice(
      0,
      6
    );

    setRecentAssigned(nextAssigned);
    setRecentCompleted(nextCompleted);

    if (nextAssigned.length || nextCompleted.length) {
      lastGoodAssigned.current = nextAssigned;
      lastGoodCompleted.current = nextCompleted;
      // persist to session cache for instant rehydration next time
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ assigned: nextAssigned, completed: nextCompleted })
        );
      } catch {
        /* ignore cache failures */
      }
    }
  };

  useEffect(() => {
    fetchFeeds();
    return () => {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    vpKey,
    refreshSignal,
    projectIdFilter,
    JSON.stringify(assigneeFilter),
  ]);

  /* ---------------- realtime (attach early) ---------------- */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const upsertAssigned = (task) => {
      const item = withProjName(task);
      if (!passesFilters(item)) return;

      setRecentAssigned((prev) => {
        const next = dedupeById([item, ...prev]).filter(passesFilters);
        const cut = sortAssigned(next).slice(0, 6);
        if (cut.length) {
          lastGoodAssigned.current = cut;
          // keep cache fresh
          try {
            const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "{}");
            sessionStorage.setItem(
              cacheKey,
              JSON.stringify({
                assigned: cut,
                completed: cached.completed || lastGoodCompleted.current,
              })
            );
          } catch {}
        }
        return cut;
      });
    };

    const removeFromAssigned = (taskId) => {
      setRecentAssigned((prev) => prev.filter((t) => t._id !== taskId));
    };

    const upsertCompleted = (task) => {
      const item = withProjName(task);
      if (!passesFilters(item)) return;

      setRecentCompleted((prev) => {
        const next = dedupeById([item, ...prev]).filter(passesFilters);
        const cut = sortCompleted(next).slice(0, 6);
        if (cut.length) {
          lastGoodCompleted.current = cut;
          // keep cache fresh
          try {
            const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "{}");
            sessionStorage.setItem(
              cacheKey,
              JSON.stringify({
                assigned: cached.assigned || lastGoodAssigned.current,
                completed: cut,
              })
            );
          } catch {}
        }
        return cut;
      });
    };

    const handleTaskCreated = ({ task }) => {
      const pid = taskProjectId(task);
      if (!inVisibleProjects(pid)) return;
      upsertAssigned(task);
    };

    const handleTaskUpdated = ({ task }) => {
      const pid = taskProjectId(task);
      if (!inVisibleProjects(pid)) return;

      if (!passesFilters(task)) {
        removeFromAssigned(task._id);
        setRecentCompleted((prev) => prev.filter((t) => t._id !== task._id));
        return;
      }

      if (String(task.status).toLowerCase() === "completed") {
        removeFromAssigned(task._id);
        upsertCompleted(task);
      } else {
        upsertAssigned(task);
      }
    };

    const handleTaskCompleted = ({ task }) => {
      const pid = taskProjectId(task);
      if (!inVisibleProjects(pid)) return;
      removeFromAssigned(task._id);
      upsertCompleted(task);
    };

    socket.on("task:created", handleTaskCreated);
    socket.on("task:updated", handleTaskUpdated);
    socket.on("task:completed", handleTaskCompleted);

    return () => {
      socket.off("task:created", handleTaskCreated);
      socket.off("task:updated", handleTaskUpdated);
      socket.off("task:completed", handleTaskCompleted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vpKey, projectIdFilter, JSON.stringify(assigneeFilter), cacheKey]);

  /* ---------------- UI helpers ---------------- */
  const formatUser = (u) => {
    if (!u) return "—";
    if (typeof u === "string") return u || "—";
    const name = (u.name || "").trim();
    const username = (u.username || "").trim();
    const email = (u.email || "").trim();
    return name || username || email || "—";
  };

  const panelTitle = { margin: "10px", fontWeight: 800, fontSize: 14 };
  const box = {
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: 8,
    padding: 8,
    margin: 10,
  };
  const small = { fontSize: 11, color: "#374151" };
  const projName = { fontSize: 12, fontWeight: 700, color: "#111827" };

  const rowBase = {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 8,
    padding: "10px 10px",
    borderBottom: "1px solid #f2f2f2",
    borderRadius: 6,
    transition: "background 120ms ease",
  };
  const rowAssigned = {
    ...rowBase,
    background: "#FEF2F2",
    borderLeft: "4px solid #FCA5A5",
  };
  const rowAssignedHover = { background: "#FEE2E2" };
  const rowCompleted = {
    ...rowBase,
    background: "#ECFDF5",
    borderLeft: "4px solid #6EE7B7",
  };
  const rowCompletedHover = { background: "#D1FAE5" };
  const rightCol = { ...small, textAlign: "right", minWidth: 72 };
  const remarkStyle = {
    fontSize: 11,
    color: "#6B7280",
    fontStyle: "italic",
    marginTop: 2,
    whiteSpace: "pre-wrap",
  };

  const HoverRow = ({ children, style, hoverStyle }) => {
    const [h, setH] = useState(false);
    return (
      <div
        style={{ ...(h ? { ...style, ...hoverStyle } : style) }}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
      >
        {children}
      </div>
    );
  };

  const assignedList = recentAssigned.length
    ? recentAssigned
    : lastGoodAssigned.current;
  const completedList = recentCompleted.length
    ? recentCompleted
    : lastGoodCompleted.current;

  /* ---------------- UI ---------------- */
  return (
    <div style={{ height: "100%", overflowY: "auto", background: "#fafafa" }}>
      <h4 style={panelTitle}>Project Task</h4>

      {/* Assigned */}
      <div style={box}>
        <div
          style={{
            ...panelTitle,
            margin: 0,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>Assigned (recent)</span>
          <span
            style={{
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 9999,
              background: "#FCA5A5",
              color: "#fff",
            }}
          >
            pending
          </span>
        </div>

        {assignedList.length === 0 ? (
          <div style={{ ...small, padding: 6, marginTop: 4 }}>
            No recent assignments.
          </div>
        ) : (
          assignedList.map((t) => (
            <HoverRow
              key={t._id}
              style={rowAssigned}
              hoverStyle={rowAssignedHover}
            >
              <div>
                <div style={projName}>{t.projectName}</div>
                <div style={small}>
                  {t.typeofProject || "-"} • {t.workType || "-"} •{" "}
                  {(t.drawingDocuments || []).join(", ")}
                </div>
                <div style={{ ...small, opacity: 0.9 }}>
                  To: {formatUser(t.assignedTo)} {" | "} By:{" "}
                  {formatUser(t.assignedBy)}
                </div>
                {!!t.remark && (
                  <div style={remarkStyle}>Remark: {t.remark}</div>
                )}
              </div>
              <div style={rightCol}>
                {t.dueDate
                  ? new Date(t.dueDate).toLocaleDateString("en-GB")
                  : "—"}
              </div>
            </HoverRow>
          ))
        )}
      </div>

      {/* Completed */}
      <div style={box}>
        <div
          style={{
            ...panelTitle,
            margin: 0,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>Completed (recent)</span>
          <span
            style={{
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 9999,
              background: "#34D399",
              color: "#064E3B",
            }}
          >
            done
          </span>
        </div>

        {completedList.length === 0 ? (
          <div style={{ ...small, padding: 6, marginTop: 4 }}>
            No recent completions.
          </div>
        ) : (
          completedList.map((t) => (
            <HoverRow
              key={t._id}
              style={rowCompleted}
              hoverStyle={rowCompletedHover}
            >
              <div>
                <div style={projName}>{t.projectName}</div>
                <div style={small}>
                  {t.typeofProject || "-"} • {t.workType || "-"} •{" "}
                  {(t.drawingDocuments || []).join(", ")}
                </div>
                <div style={{ ...small, opacity: 0.9 }}>
                  By: {formatUser(t.assignedBy)} {" → "}{" "}
                  {formatUser(t.assignedTo)}
                </div>
                {!!t.remark && (
                  <div style={remarkStyle}>Remark: {t.remark}</div>
                )}
              </div>
              <div style={rightCol}>
                {t.completedAt
                  ? new Date(t.completedAt).toLocaleDateString("en-GB")
                  : "—"}
              </div>
            </HoverRow>
          ))
        )}
      </div>
    </div>
  );
}
